import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { 
  ArrowLeft, Flag, Check, X, ChevronDown, ChevronRight,
  Loader2, RefreshCw, FileText, User, Calendar, ExternalLink,
  AlertTriangle, MessageSquare, Edit, Save, Eye
} from 'lucide-react';
import { toast } from 'sonner';

// TipTap Editor imports
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { Highlight } from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Grade configurations
const ALL_GRADES = [
  { value: 'all', label: 'All Grades' },
  { value: 'MEO Class 1', label: 'MEO Class 1' },
  { value: 'MEO Class 2', label: 'MEO Class 2' },
  { value: 'MEO Class 4', label: 'MEO Class 4' },
  { value: 'ETO', label: 'ETO' },
  { value: 'Master', label: 'Master' },
  { value: 'Chief Mate', label: 'Chief Mate' },
  { value: '2MFG', label: '2MFG' }
];

const DEPARTMENTS = [
  { value: 'all', label: 'All Departments' },
  { value: 'Engineering', label: 'Engineering' },
  { value: 'Deck', label: 'Deck' }
];

const REASON_TYPE_LABELS = {
  'incorrect_info': 'Incorrect Information',
  'outdated': 'Outdated Content',
  'typo': 'Typo/Grammar',
  'typo_grammar': 'Typo/Grammar',
  'missing_content': 'Missing Content',
  'confusing': 'Confusing Explanation',
  'other': 'Other'
};

const REASON_TYPE_COLORS = {
  'incorrect_info': 'bg-red-100 text-red-700',
  'outdated': 'bg-amber-100 text-amber-700',
  'typo': 'bg-blue-100 text-blue-700',
  'typo_grammar': 'bg-blue-100 text-blue-700',
  'missing_content': 'bg-purple-100 text-purple-700',
  'confusing': 'bg-orange-100 text-orange-700',
  'other': 'bg-gray-100 text-gray-700'
};

// Simple Editor Toolbar
const EditorToolbar = ({ editor }) => {
  if (!editor) return null;
  
  return (
    <div className="flex flex-wrap gap-1 p-2 border-b bg-gray-50 rounded-t-lg">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
        title="Bold"
      >
        <span className="font-bold text-sm">B</span>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
        title="Italic"
      >
        <span className="italic text-sm">I</span>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('underline') ? 'bg-gray-200' : ''}`}
        title="Underline"
      >
        <span className="underline text-sm">U</span>
      </button>
      <div className="w-px bg-gray-300 mx-1" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''}`}
        title="Heading"
      >
        <span className="font-bold text-sm">H2</span>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 3 }) ? 'bg-gray-200' : ''}`}
        title="Subheading"
      >
        <span className="font-bold text-sm">H3</span>
      </button>
      <div className="w-px bg-gray-300 mx-1" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-200' : ''}`}
        title="Bullet List"
      >
        <span className="text-sm">•</span>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-200' : ''}`}
        title="Numbered List"
      >
        <span className="text-sm">1.</span>
      </button>
      <div className="w-px bg-gray-300 mx-1" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleHighlight().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('highlight') ? 'bg-yellow-200' : ''}`}
        title="Highlight"
      >
        <span className="text-sm bg-yellow-200 px-1">H</span>
      </button>
    </div>
  );
};

const FlaggedReportsPage = () => {
  const navigate = useNavigate();
  
  // Filters
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('pending');
  
  // Data
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReports, setExpandedReports] = useState({});
  
  // Stats
  const [stats, setStats] = useState({ pending: 0, resolved: 0, dismissed: 0 });
  
  // Editor Drawer State
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [noteTitle, setNoteTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingNote, setLoadingNote] = useState(false);

  // TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Link.configure({ openOnClick: false }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight.configure({ multicolor: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[300px] p-4',
      },
    },
  });

  // Fetch reports
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = { status: selectedStatus };
      if (selectedGrade !== 'all') params.grade = selectedGrade;
      if (selectedDepartment !== 'all') params.department = selectedDepartment;
      
      const response = await axios.get(`${API}/notes/reports/all`, { params });
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load flagged reports');
    } finally {
      setLoading(false);
    }
  }, [selectedGrade, selectedDepartment, selectedStatus]);

  // Fetch stats (counts by status)
  const fetchStats = useCallback(async () => {
    try {
      const [pendingRes, resolvedRes, dismissedRes] = await Promise.all([
        axios.get(`${API}/notes/reports/all`, { params: { status: 'pending' } }),
        axios.get(`${API}/notes/reports/all`, { params: { status: 'resolved' } }),
        axios.get(`${API}/notes/reports/all`, { params: { status: 'dismissed' } })
      ]);
      setStats({
        pending: pendingRes.data.total || 0,
        resolved: resolvedRes.data.total || 0,
        dismissed: dismissedRes.data.total || 0
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Toggle report expansion
  const toggleReport = (reportId) => {
    setExpandedReports(prev => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  // Update report status
  const updateReportStatus = async (reportId, newStatus, resolutionNote = '') => {
    try {
      await axios.put(`${API}/notes/reports/${reportId}/status`, {
        status: newStatus,
        resolution_note: resolutionNote
      });
      toast.success(`Report ${newStatus}`);
      fetchReports();
      fetchStats();
    } catch (error) {
      console.error('Failed to update report:', error);
      toast.error('Failed to update report');
    }
  };

  // Open editor drawer with note content
  const openEditor = async (report) => {
    setEditingReport(report);
    setLoadingNote(true);
    setEditorOpen(true);
    
    try {
      const response = await axios.get(`${API}/notes/${report.note_id}`);
      const note = response.data;
      setEditingNote(note);
      setNoteTitle(note.title || '');
      if (editor) {
        editor.commands.setContent(note.content || '');
      }
    } catch (error) {
      console.error('Failed to load note:', error);
      toast.error('Failed to load chapter content');
      setEditorOpen(false);
    } finally {
      setLoadingNote(false);
    }
  };

  // Save note changes
  const saveNote = async () => {
    if (!editingNote || !editor) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/notes/${editingNote.id}`, {
        title: noteTitle,
        content: editor.getHTML(),
      });
      toast.success('Chapter saved successfully');
      return true;
    } catch (error) {
      console.error('Failed to save note:', error);
      toast.error('Failed to save chapter');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Save and resolve report
  const saveAndResolve = async () => {
    const saved = await saveNote();
    if (saved && editingReport) {
      await updateReportStatus(editingReport.id, 'resolved', 'Content updated');
      setEditorOpen(false);
      setEditingReport(null);
      setEditingNote(null);
    }
  };

  // Save and close without resolving
  const saveAndClose = async () => {
    const saved = await saveNote();
    if (saved) {
      setEditorOpen(false);
      setEditingReport(null);
      setEditingNote(null);
    }
  };

  // Navigate to chapter editor (full page) - Fixed grade handling
  const viewChapter = (noteInfo) => {
    const grade = noteInfo.grade || 'MEO Class 1';
    const isEngineering = grade.includes('MEO') || grade === 'ETO';
    const dept = isEngineering ? 'engineering' : 'deck';
    
    // Store in sessionStorage for the target page to read
    sessionStorage.setItem('openNoteContext', JSON.stringify({
      noteId: noteInfo.id,
      grade: grade
    }));
    
    navigate(`/admin/notes/${dept}?grade=${encodeURIComponent(grade)}&noteId=${noteInfo.id}`);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout>
      <div className="page-transition min-h-[calc(100vh-120px)]">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(-1)}
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="p-2 bg-red-100 rounded-lg">
              <Flag className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Flagged Reports</h1>
              <p className="text-sm text-gray-500">
                Review and manage reported issues in notes
              </p>
            </div>
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { fetchReports(); fetchStats(); }}
            data-testid="refresh-button"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card 
            className={`cursor-pointer transition-all ${selectedStatus === 'pending' ? 'ring-2 ring-amber-500' : ''}`}
            onClick={() => setSelectedStatus('pending')}
            data-testid="pending-stat-card"
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                <p className="text-sm text-gray-500">Pending</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all ${selectedStatus === 'resolved' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => setSelectedStatus('resolved')}
            data-testid="resolved-stat-card"
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
                <p className="text-sm text-gray-500">Resolved</p>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer transition-all ${selectedStatus === 'dismissed' ? 'ring-2 ring-gray-500' : ''}`}
            onClick={() => setSelectedStatus('dismissed')}
            data-testid="dismissed-stat-card"
          >
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-600">{stats.dismissed}</p>
                <p className="text-sm text-gray-500">Dismissed</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Grade:</span>
                <Select value={selectedGrade} onValueChange={setSelectedGrade}>
                  <SelectTrigger className="w-40" data-testid="grade-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ALL_GRADES.map(g => (
                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Department:</span>
                <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                  <SelectTrigger className="w-40" data-testid="department-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map(d => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-gray-500">
                  Showing {reports.length} {selectedStatus} reports
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reports List */}
        <Card>
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Flag className="w-4 h-4" />
              {selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)} Reports
            </CardTitle>
          </CardHeader>
          <ScrollArea className="max-h-[calc(100vh-420px)]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-12">
                <Flag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-gray-600 font-medium mb-1">No {selectedStatus} reports</h3>
                <p className="text-sm text-gray-500">
                  {selectedStatus === 'pending' 
                    ? 'All caught up! No reports need attention.' 
                    : `No ${selectedStatus} reports found.`}
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {reports.map((report) => {
                  const isExpanded = expandedReports[report.id];
                  const noteInfo = report.note_info || {};
                  
                  return (
                    <div key={report.id} className="hover:bg-gray-50 transition-colors">
                      {/* Report Row */}
                      <div 
                        className="flex items-center gap-4 p-4 cursor-pointer"
                        onClick={() => toggleReport(report.id)}
                        data-testid={`report-row-${report.id}`}
                      >
                        <button className="p-1 hover:bg-gray-200 rounded">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          )}
                        </button>
                        
                        {/* Reason Type Badge */}
                        <Badge className={`${REASON_TYPE_COLORS[report.reason_type] || 'bg-gray-100 text-gray-700'} text-xs`}>
                          {REASON_TYPE_LABELS[report.reason_type] || report.reason_type}
                        </Badge>
                        
                        {/* Chapter Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="font-medium text-gray-900 truncate">
                              {noteInfo.title || 'Unknown Chapter'}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                            <span>{noteInfo.grade || 'N/A'}</span>
                            <span>•</span>
                            <span className="truncate">{noteInfo.topic_id || 'N/A'}</span>
                          </div>
                        </div>
                        
                        {/* Reporter & Date */}
                        <div className="text-right flex-shrink-0">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <User className="w-3 h-3" />
                            {report.user_name || 'Anonymous'}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            <Calendar className="w-3 h-3" />
                            {formatDate(report.created_at)}
                          </div>
                        </div>
                        
                        {/* Actions (only for pending) */}
                        {selectedStatus === 'pending' && (
                          <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-300 hover:bg-green-50"
                              onClick={() => updateReportStatus(report.id, 'resolved')}
                              data-testid={`resolve-btn-${report.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Resolve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-gray-600 border-gray-300 hover:bg-gray-50"
                              onClick={() => updateReportStatus(report.id, 'dismissed')}
                              data-testid={`dismiss-btn-${report.id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Dismiss
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="px-4 pb-4 ml-10 border-l-2 border-gray-200">
                          {/* Report Description */}
                          {report.description && (
                            <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-start gap-2">
                                <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-gray-700 mb-1">Report Details</p>
                                  <p className="text-sm text-gray-600">{report.description}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Resolution Note (for resolved/dismissed) */}
                          {report.resolution_note && (
                            <div className="mb-3 p-3 bg-green-50 rounded-lg">
                              <p className="text-sm font-medium text-green-700 mb-1">Resolution Note</p>
                              <p className="text-sm text-green-600">{report.resolution_note}</p>
                            </div>
                          )}
                          
                          {/* Quick Actions */}
                          <div className="flex items-center gap-2">
                            {/* Edit Chapter Button - Opens inline editor */}
                            {selectedStatus === 'pending' && (
                              <Button
                                size="sm"
                                onClick={() => openEditor(report)}
                                className="bg-[#14B8A6] hover:bg-[#0D9488] text-white"
                                data-testid={`edit-chapter-btn-${report.id}`}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit Chapter
                              </Button>
                            )}
                            
                            {/* View in Full Editor */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => viewChapter(noteInfo)}
                              data-testid={`view-chapter-btn-${report.id}`}
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Open Full Editor
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </Card>
      </div>

      {/* Inline Editor Sheet/Drawer */}
      <Sheet open={editorOpen} onOpenChange={setEditorOpen}>
        <SheetContent side="right" className="w-[700px] sm:max-w-[700px] p-0 flex flex-col">
          <SheetHeader className="p-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#14B8A6]" />
              Edit Chapter
            </SheetTitle>
            <SheetDescription>
              {editingReport && (
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`${REASON_TYPE_COLORS[editingReport.reason_type] || 'bg-gray-100 text-gray-700'} text-xs`}>
                    {REASON_TYPE_LABELS[editingReport.reason_type] || editingReport.reason_type}
                  </Badge>
                  <span className="text-gray-500">reported by {editingReport.user_name || 'Anonymous'}</span>
                </div>
              )}
            </SheetDescription>
          </SheetHeader>
          
          {loadingNote ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Report Description Banner */}
              {editingReport?.description && (
                <div className="mx-4 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Issue Reported:</p>
                      <p className="text-sm text-amber-700">{editingReport.description}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Title Input */}
              <div className="px-4 pt-4">
                <Label htmlFor="note-title" className="text-sm font-medium">Chapter Title</Label>
                <Input
                  id="note-title"
                  value={noteTitle}
                  onChange={(e) => setNoteTitle(e.target.value)}
                  className="mt-1"
                  placeholder="Enter chapter title..."
                  data-testid="chapter-title-input"
                />
              </div>
              
              {/* Editor */}
              <div className="flex-1 px-4 py-4 overflow-hidden flex flex-col">
                <Label className="text-sm font-medium mb-2">Content</Label>
                <div className="flex-1 border rounded-lg overflow-hidden flex flex-col">
                  <EditorToolbar editor={editor} />
                  <ScrollArea className="flex-1">
                    <EditorContent editor={editor} className="min-h-[300px]" />
                  </ScrollArea>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditorOpen(false);
                    setEditingReport(null);
                    setEditingNote(null);
                  }}
                >
                  Cancel
                </Button>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={saveAndClose}
                    disabled={saving}
                    data-testid="save-only-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Save Only
                  </Button>
                  
                  <Button
                    onClick={saveAndResolve}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="save-and-resolve-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                    Save & Resolve
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </Layout>
  );
};

export default FlaggedReportsPage;
